import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  NotificationsActive as NotifyIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { TABLES, getCurrentSchoolYear, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';
import SchoolYearBadge from '../components/SchoolYearBadge';
import {
  CATEGORY_KEYS,
  buildDataset,
  buildFeatureVectors,
  buildProfiles,
  buildRetrospective,
  forecastEndOfYear,
  giniCoefficient,
  kMeans,
  max,
  mean,
  median,
  min,
  normalizedEntropy,
  recommend,
  schoolMonthIndex,
  stdDev,
} from '../lib/intelligence';
import { downloadExcel, getPreviousSchoolYear } from '../lib/statistics';

const heatColor = (value, maxValue) => {
  if (!value) return '#ffffff';
  const ratio = maxValue > 0 ? value / maxValue : 0;
  if (ratio <= 0.25) return '#d1fae5';
  if (ratio <= 0.5) return '#6ee7b7';
  if (ratio <= 0.75) return '#10b981';
  return '#047857';
};

const SummaryCard = ({ label, value, suffix, color }) => (
  <Card sx={{ borderRadius: 3, flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Typography variant="h3" fontWeight="bold" color={color} sx={{ fontSize: { xs: '1.4rem', sm: '1.8rem' } }}>
        {value}{suffix || ''}
      </Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, height = 300, children, action }) => (
  <Card sx={{ borderRadius: 3, height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
        {action}
      </Box>
      <Box sx={{ width: '100%', height, minWidth: 0 }}>
        <ResponsiveContainer initialDimension={{ width: 520, height }}>{children}</ResponsiveContainer>
      </Box>
    </CardContent>
  </Card>
);

const statusChip = (status) => {
  if (status === 'risc') return <Chip size="small" color="error" label="La risc" />;
  if (status === 'sub') return <Chip size="small" color="warning" label="Sub medie" />;
  return <Chip size="small" color="success" label="OK" />;
};

const CATEGORY_LABEL_MAP = {
  curriculare: 'Curriculare',
  extracurriculare: 'Extracurriculare',
  transferabile: 'Transferabile',
};

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const TabSkeleton = () => (
  <Box sx={{ display: 'grid', gap: 2 }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
      {[0, 1, 2, 3].map((item) => (
        <Skeleton key={item} variant="rectangular" height={90} sx={{ borderRadius: 3 }} />
      ))}
    </Box>
    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
    <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 3 }} />
  </Box>
);

const EMPTY_METRICS = {
  sessionCount: 0,
  shownCount: 0,
  clickedCount: 0,
  convertedCount: 0,
  precision: null,
  recall: null,
  f1: null,
  ndcg: null,
  diversity: null,
  moduleDistribution: [],
  positionDistribution: [1, 2, 3, 4, 5].map((position) => ({ position: `#${position}`, clickuri: 0 })),
  coverage: null,
  evolution: [],
};

const formatPercent = (ratio) =>
  ratio === null || ratio === undefined ? 'date insuficiente' : `${(ratio * 100).toFixed(0)}%`;

const formatNumber = (value, digits = 2) =>
  value === null || value === undefined ? 'date insuficiente' : value.toFixed(digits);

export default function Intelligence() {
  const { profile } = useAuth();
  const [tab, setTab] = useState(0);
  const [yearOptions, setYearOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [retroYear, setRetroYear] = useState(null);
  const [filters, setFilters] = useState({ clasa: 'all', spec: 'all' });
  const [dashFilters, setDashFilters] = useState({ clasa: 'all', spec: 'all' });
  const [selectedStudentId, setSelectedStudentId] = useState('all');
  const [sort, setSort] = useState({ field: 'total', dir: 'desc' });
  const [drawerStudent, setDrawerStudent] = useState(null);
  const [raw, setRaw] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [batchDialog, setBatchDialog] = useState({ open: false, clasa: null, items: [] });
  const [alertsDialog, setAlertsDialog] = useState({ open: false, title: '', items: [] });
  const [resetDialog, setResetDialog] = useState(false);
  const loadingRef = useRef(true);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const debouncedFilters = useDebouncedValue(filters);
  const debouncedDashFilters = useDebouncedValue(dashFilters);
  const debouncedStudentId = useDebouncedValue(selectedStudentId);
  const computing =
    filters !== debouncedFilters ||
    dashFilters !== debouncedDashFilters ||
    selectedStudentId !== debouncedStudentId;

  useEffect(() => {
    let mounted = true;
    const timeout = setTimeout(() => {
      if (mounted && loadingRef.current) {
        setError('Eroare la incarcare. Reincarca pagina.');
        setLoading(false);
      }
    }, 10000);
    const load = async () => {
      try {
        const [studentsRes, requestsRes, categoriesRes, activitiesRes, modulesRes, typesRes, calendarRes, auditRes, years] =
          await Promise.all([
            supabase.from(TABLES.profiles).select('id, nume, prenume, clasa, specializare').eq('rol', 'elev'),
            supabase
              .from(TABLES.credit_requests)
              .select('id, id_elev, id_tip_credit, id_module, id_activitate_predefinita, nume_activitate, credite_solicitate, credite_acordate, status, data_cerere, data_validare, an_scolar, source_system')
              .eq('status', 'validat'),
            supabase.from(TABLES.credits_by_category).select('id_elev, an_scolar, credite_curriculare, credite_extracurriculare, credite_transferabile, credite_totale'),
            supabase
              .from(TABLES.predefined_activities)
              .select('id, id_tip_credit, id_module, nume_activitate, descriere, ore_recomandate, credite_per_ora, credite_acordate, este_activ')
              .eq('este_activ', true),
            supabase.from(TABLES.modules).select('id, cod_module, nume_module, credite_tinta, ordine').order('ordine'),
            supabase.from(TABLES.credit_types).select('id, nume_tip, categorie'),
            supabase.from('academic_calendar_v2').select('an_scolar, id_module, data_start, data_end'),
            supabase
              .from(TABLES.audit_log)
              .select('id, created_at, entity_type, entity_name, new_values')
              .in('entity_type', ['suggestion_shown', 'suggestion_clicked'])
              .order('created_at', { ascending: false })
              .limit(2000),
            getSchoolYears(TABLES.credit_requests),
          ]);
        if (!mounted) return;
        setRaw({
          students: studentsRes.data || [],
          requests: requestsRes.data || [],
          categories: categoriesRes.data || [],
          activities: activitiesRes.data || [],
          modules: modulesRes.data || [],
          creditTypes: typesRes.data || [],
          calendar: calendarRes.data || [],
        });
        setAuditEvents(auditRes.data || []);
        setYearOptions(years);
        setSelectedYear((previous) => previous ?? resolveSchoolYear(years));
        setRetroYear((previous) => previous ?? getPreviousSchoolYear(resolveSchoolYear(years)));
        setLoading(false);
      } catch (err) {
        console.error('Intelligence load error:', err);
        if (!mounted) return;
        setError('Eroare la incarcare. Reincarca pagina.');
        setLoading(false);
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    return () => { mounted = false; clearTimeout(timeout); };
  }, []);

  const dataset = useMemo(() => (raw ? buildDataset(raw) : null), [raw]);

  const profiles = useMemo(
    () => (dataset && selectedYear && selectedYear !== 'all' ? buildProfiles(dataset, selectedYear) : new Map()),
    [dataset, selectedYear],
  );
  const retroProfiles = useMemo(
    () => (dataset && retroYear ? buildProfiles(dataset, retroYear) : new Map()),
    [dataset, retroYear],
  );

  const cluster = useMemo(
    () => (profiles.size === 0 || !drawerStudent
      ? { clusters: new Map(), members: [] }
      : kMeans(buildFeatureVectors(profiles), 3)),
    [profiles, drawerStudent],
  );

  const recommendationsByStudent = useMemo(() => {
    if (!dataset || profiles.size === 0 || tab !== 3) return new Map();
    const map = new Map();
    profiles.forEach((profile) => {
      map.set(profile.studentId, recommend(dataset, profiles, profile.studentId, { k: 5 }));
    });
    return map;
  }, [dataset, profiles, tab]);

  const drawerRecommendations = useMemo(() => {
    const profile = drawerStudent ? profiles.get(drawerStudent.id) : null;
    if (!dataset || !profile) return [];
    return recommend(dataset, profiles, profile.studentId, { k: 5 });
  }, [dataset, profiles, drawerStudent]);

  const students = useMemo(() => {
    if (profiles.size === 0) return [];
    const totals = [...profiles.values()].map((profile) => profile.totalCredits);
    return [...profiles.values()].map((profile) => {
      const classMates = [...profiles.values()].filter((entry) => entry.clasa === profile.clasa);
      const classMean = mean(classMates.map((entry) => entry.totalCredits));
      return {
        id: profile.studentId,
        nume: profile.nume,
        prenume: profile.prenume,
        clasa: profile.clasa,
        specializare: profile.specializare,
        total: profile.totalCredits,
        modules: profile.creditsByModule,
        categories: profile.creditsByCategory,
        diversityScore: profile.diversityScore,
        consistency: profile.consistency,
        percentile: profile.percentileInSchool,
        growthSlope: profile.growthSlope,
        activityCount: profile.activityCount,
        status: profile.totalCredits === 0 ? 'risc' : profile.totalCredits < classMean ? 'sub' : 'ok',
        classMean,
        rank: classMates.filter((entry) => entry.totalCredits > profile.totalCredits).length + 1,
        classSize: classMates.length,
        schoolMean: mean(totals),
      };
    });
  }, [profiles]);

  const classes = useMemo(() => {
    const groups = new Map();
    students.forEach((student) => {
      const key = student.clasa || 'Fara clasa';
      const list = groups.get(key) || [];
      list.push(student);
      groups.set(key, list);
    });
    const currentMonthIndex = Math.min(9, Math.max(0, schoolMonthIndex(new Date().toISOString())));
    const schoolMean = mean(students.map((student) => student.total));
    const last30 = Date.now() - 30 * 86400000;
    return [...groups.entries()].map(([clasa, list]) => {
      const totals = list.map((student) => student.total);
      const moduleTotals = dataset.moduleCodes.map((code) =>
        list.reduce((sum, student) => sum + (student.modules[code] || 0), 0),
      );
      const categoryTotals = CATEGORY_KEYS.map((key) =>
        list.reduce((sum, student) => sum + (student.categories?.[key] || 0), 0),
      );
      const activeCount = list.filter((student) => student.activityCount > 0).length;
      const ids = new Set(list.map((student) => student.id));
      const monthSums = new Array(10).fill(0);
      (dataset.requests || [])
        .filter((request) => request.an_scolar === selectedYear && ids.has(request.id_elev))
        .forEach((request) => {
          const index = schoolMonthIndex(request.data_validare || request.data_cerere);
          if (index >= 0 && index < 10) {
            monthSums[index] += request.credite_acordate || request.credite_solicitate || 0;
          }
        });
      const currentMonth = monthSums[currentMonthIndex] || 0;
      const previousMonth = currentMonthIndex > 0 ? monthSums[currentMonthIndex - 1] || 0 : null;
      const trend30 = (dataset.requests || [])
        .filter(
          (request) =>
            request.an_scolar === selectedYear &&
            ids.has(request.id_elev) &&
            new Date(request.data_validare || request.data_cerere).getTime() >= last30,
        )
        .reduce((sum, request) => sum + (request.credite_acordate || request.credite_solicitate || 0), 0);
      const classMean = mean(totals);
      return {
        clasa,
        elevi: list.length,
        medie: classMean,
        median: median(totals),
        std: stdDev(totals),
        min: min(totals),
        max: max(totals),
        entropie: moduleTotals.reduce((sum, value) => sum + value, 0) > 0 ? normalizedEntropy(moduleTotals) : null,
        gini: giniCoefficient(totals),
        zero: list.filter((student) => student.total === 0).length,
        atRisk: list.filter((student) => student.total === 0 || (dataset.targetSum > 0 && student.total < dataset.targetSum * 0.25)).length,
        excellence: list.filter((student) => dataset.targetSum > 0 && student.total >= dataset.targetSum * 0.8).length,
        trend: previousMonth && previousMonth > 0 ? ((currentMonth - previousMonth) / previousMonth) * 100 : null,
        hasPreviousMonth: previousMonth !== null,
        categoryTotals,
        activeCount,
        inactiveCount: list.length - activeCount,
        trend30,
        schoolDelta: schoolMean > 0 ? ((classMean - schoolMean) / schoolMean) * 100 : null,
        students: list,
      };
    }).sort((a, b) => a.clasa.localeCompare(b.clasa));
  }, [students, dataset, selectedYear]);

  const classOptions = useMemo(() => [...new Set(students.map((student) => student.clasa).filter(Boolean))].sort(), [students]);
  const specOptions = useMemo(() => [...new Set(students.map((student) => student.specializare).filter(Boolean))].sort(), [students]);

  const dashStudents = useMemo(
    () =>
      students.filter(
        (student) =>
          (debouncedDashFilters.clasa === 'all' || student.clasa === debouncedDashFilters.clasa) &&
          (debouncedDashFilters.spec === 'all' || student.specializare === debouncedDashFilters.spec) &&
          (debouncedStudentId === 'all' || student.id === debouncedStudentId),
      ),
    [students, debouncedDashFilters, debouncedStudentId],
  );
  const dashStudentIds = useMemo(() => new Set(dashStudents.map((student) => student.id)), [dashStudents]);

  const filteredStudents = useMemo(() => {
    let list = [...students];
    if (debouncedStudentId !== 'all') list = list.filter((student) => student.id === debouncedStudentId);
    if (debouncedFilters.clasa !== 'all') list = list.filter((student) => student.clasa === debouncedFilters.clasa);
    if (debouncedFilters.spec !== 'all') list = list.filter((student) => student.specializare === debouncedFilters.spec);
    const { field, dir } = sort;
    list.sort((a, b) => {
      const values = {
        nume: `${a.nume} ${a.prenume}`,
        clasa: a.clasa,
        specializare: a.specializare,
        total: a.total,
        diversityScore: a.diversityScore,
        consistency: a.consistency ?? 0,
        percentile: a.percentile,
        modules: dataset.moduleCodes.reduce((sum, code) => sum + (a.modules[code] || 0), 0),
      };
      const other = {
        nume: `${b.nume} ${b.prenume}`,
        clasa: b.clasa,
        specializare: b.specializare,
        total: b.total,
        diversityScore: b.diversityScore,
        consistency: b.consistency ?? 0,
        percentile: b.percentile,
        modules: dataset.moduleCodes.reduce((sum, code) => sum + (b.modules[code] || 0), 0),
      };
      const aValue = values[field];
      const bValue = other[field];
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return dir === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return dir === 'asc'
        ? String(aValue || '').localeCompare(String(bValue || ''))
        : String(bValue || '').localeCompare(String(aValue || ''));
    });
    return list;
  }, [students, debouncedFilters, sort, dataset, debouncedStudentId]);

  const globalTrend = useMemo(() => {
    if (tab !== 0) return [];
    const monthly = new Array(10).fill(0);
    (dataset?.requests || [])
      .filter(
        (request) =>
          request.an_scolar === selectedYear &&
          dashStudentIds.has(request.id_elev) &&
          !String(request.nume_activitate || '').startsWith('TEST_'),
      )
      .forEach((request) => {
        const index = schoolMonthIndex(request.data_validare || request.data_cerere);
        if (index >= 0 && index < 10) {
          monthly[index] += request.credite_acordate || request.credite_solicitate || 0;
        }
      });
    let running = 0;
    return ['sep', 'oct', 'nov', 'dec', 'ian', 'feb', 'mar', 'apr', 'mai', 'iun'].map((luna, index) => {
      running += monthly[index];
      return { luna, credite: monthly[index], cumulat: running };
    });
  }, [dataset, selectedYear, dashStudentIds, tab]);

  const sourceCounts = useMemo(() => {
    const counts = { manual: 0, srbhub: 0, import: 0 };
    (dataset?.requests || [])
      .filter((request) => request.an_scolar === selectedYear && dashStudentIds.has(request.id_elev))
      .forEach((request) => {
        if (request.source_system === 'srbhub') counts.srbhub += 1;
        else if (request.nume_activitate?.startsWith('TEST_')) counts.import += 1;
        else counts.manual += 1;
      });
    return counts;
  }, [dataset, selectedYear, dashStudentIds]);

  const alerts = useMemo(() => {
    const atRisk = dashStudents
      .filter(
        (student) =>
          student.activityCount === 0 &&
          (dataset?.targetSum || 0) > 0 &&
          student.total < (dataset.targetSum || 0) * 0.5,
      )
      .sort((a, b) => a.total - b.total);
    const moduleGaps = dataset
      ? dataset.modules.map((module) => ({
          cod: module.cod_module,
          deficit: Math.max(
            0,
            (module.credite_tinta || 0) - dashStudents.reduce((sum, student) => sum + (student.modules[module.cod_module] || 0), 0),
          ),
        })).sort((a, b) => b.deficit - a.deficit).slice(0, 2)
      : [];
    const classNames = [...new Set(dashStudents.map((student) => student.clasa).filter(Boolean))];
    const scopedClasses = classes.filter((entry) => classNames.includes(entry.clasa));
    const weakClasses = scopedClasses
      .filter((entry) => scopedClasses.length > 1 && entry.medie < mean(scopedClasses.map((item) => item.medie)))
      .slice(0, 3);
    return {
      atRisk,
      moduleGaps,
      weakClasses,
      total: atRisk.length + moduleGaps.length + weakClasses.length,
    };
  }, [dashStudents, classes, dataset]);

  const studentOptions = useMemo(
    () =>
      [...students]
        .sort((a, b) => `${a.nume} ${a.prenume}`.localeCompare(`${b.nume} ${b.prenume}`))
        .map((student) => ({
          id: student.id,
          label: `${student.nume} ${student.prenume} (${student.clasa || '-'})`,
        })),
    [students],
  );
  const selectedStudentOption = studentOptions.find((option) => option.id === selectedStudentId) || null;

  const selectedStudent = useMemo(
    () => (selectedStudentId === 'all' ? null : students.find((student) => student.id === selectedStudentId) || null),
    [students, selectedStudentId],
  );

  useEffect(() => {
    if (!selectedStudent) return;
    setDrawerStudent(selectedStudent);
  }, [selectedStudent]);

  const retrospective = useMemo(() => {
    if (!dataset || retroProfiles.size === 0 || tab !== 4) return [];
    return [...retroProfiles.values()]
      .filter((profile) => profile.activityCount > 0)
      .filter((profile) => debouncedStudentId === 'all' || profile.studentId === debouncedStudentId)
      .sort((a, b) => b.totalCredits - a.totalCredits)
      .slice(0, 15)
      .map((profile) => {
        const report = buildRetrospective(dataset, retroProfiles, profile.studentId);
        const current = profiles.get(profile.studentId);
        const delta = current && profile.totalCredits > 0
          ? ((current.totalCredits - profile.totalCredits) / profile.totalCredits) * 100
          : null;
        return {
          report: report ? { ...report, studentName: `${profile.nume} ${profile.prenume}` } : null,
          delta,
          currentTotal: current?.totalCredits ?? null,
        };
      })
      .filter((entry) => entry.report);
  }, [dataset, retroProfiles, profiles, debouncedStudentId, tab]);

  const yearRange = useMemo(() => {
    if (!selectedYear || selectedYear === 'all') return null;
    const startYear = Number(String(selectedYear).slice(0, 4));
    return { start: new Date(startYear, 8, 1), end: new Date(startYear + 1, 8, 1) };
  }, [selectedYear]);

  const metrics = useMemo(() => {
    if (tab !== 5) return EMPTY_METRICS;
    const inYear = (event) =>
      !yearRange ||
      (new Date(event.created_at) >= yearRange.start && new Date(event.created_at) < yearRange.end);
    const inScope = (event) =>
      debouncedStudentId === 'all' || event.new_values?.id_elev === debouncedStudentId;
    const sessions = auditEvents
      .filter(
        (event) =>
          event.entity_type === 'suggestion_shown' &&
          inYear(event) &&
          inScope(event) &&
          Array.isArray(event.new_values?.suggestions),
      )
      .map((event) => ({
        sessionId: event.new_values.session_id || String(event.id),
        studentId: event.new_values.id_elev,
        shownAt: new Date(event.new_values.shown_at || event.created_at),
        suggestions: event.new_values.suggestions,
      }));
    const sessionIds = new Set(sessions.map((session) => session.sessionId));
    const clickEvents = auditEvents.filter(
      (event) =>
        event.entity_type === 'suggestion_clicked' &&
        inYear(event) &&
        inScope(event) &&
        sessionIds.has(event.new_values?.session_id),
    );

    const totalShown = sessions.reduce((sum, session) => sum + session.suggestions.length, 0);
    const totalClicks = clickEvents.length;
    const precision = totalShown > 0 ? totalClicks / totalShown : null;

    const activityNames = new Map(
      (dataset?.activities || []).map((activity) => [String(activity.id), activity.nume_activitate]),
    );
    const normalizeName = (value) =>
      String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const requestsByStudent = new Map();
    (dataset?.requests || []).forEach((request) => {
      const list = requestsByStudent.get(request.id_elev) || [];
      list.push(request);
      requestsByStudent.set(request.id_elev, list);
    });
    const isConverted = (session, suggestion) => {
      const limit = session.shownAt.getTime() + 30 * 86400000;
      const targetName = normalizeName(suggestion.nume || activityNames.get(String(suggestion.activity_id)));
      return (requestsByStudent.get(session.studentId) || []).some((request) => {
        const timestamp = new Date(request.data_validare || request.data_cerere).getTime();
        if (timestamp < session.shownAt.getTime() || timestamp > limit) return false;
        if (suggestion.activity_id && String(request.id_activitate_predefinita) === String(suggestion.activity_id)) {
          return true;
        }
        return Boolean(targetName) && normalizeName(request.nume_activitate) === targetName;
      });
    };
    let converted = 0;
    sessions.forEach((session) => {
      session.suggestions.forEach((suggestion) => {
        if (isConverted(session, suggestion)) converted += 1;
      });
    });
    const recall = totalShown > 0 ? converted / totalShown : null;

    const f1 = precision === null || recall === null
      ? null
      : precision === 0 || recall === 0
        ? 0
        : (2 * precision * recall) / (precision + recall);

    const clicksBySession = new Map();
    clickEvents.forEach((event) => {
      const key = event.new_values?.session_id;
      const set = clicksBySession.get(key) || new Set();
      const position = Number(event.new_values?.position);
      if (position > 0) set.add(position);
      clicksBySession.set(key, set);
    });
    const ndcgValues = [];
    sessions.forEach((session) => {
      const clicked = clicksBySession.get(session.sessionId) || new Set();
      const top = session.suggestions.slice(0, 5);
      const dcg = top.reduce(
        (sum, suggestion, index) =>
          sum + ((clicked.has(index + 1) || clicked.has(Number(suggestion.position))) ? 1 / Math.log2(index + 2) : 0),
        0,
      );
      const idcg = top.reduce((sum, suggestion, index) => sum + 1 / Math.log2(index + 2), 0);
      if (idcg > 0) ndcgValues.push(dcg / idcg);
    });
    const ndcg = ndcgValues.length > 0 ? mean(ndcgValues) : null;

    const moduleCounts = new Map();
    const sessionEntropies = [];
    sessions.forEach((session) => {
      const counts = new Map();
      session.suggestions.forEach((suggestion) => {
        if (!suggestion.module) return;
        counts.set(suggestion.module, (counts.get(suggestion.module) || 0) + 1);
        moduleCounts.set(suggestion.module, (moduleCounts.get(suggestion.module) || 0) + 1);
      });
      if (counts.size > 1) sessionEntropies.push(normalizedEntropy([...counts.values()]));
    });
    const diversity = sessionEntropies.length > 0 ? mean(sessionEntropies) : null;
    const moduleDistribution = dataset
      ? dataset.moduleCodes.map((code) => ({ cod: code, count: moduleCounts.get(code) || 0 }))
      : [];

    const positionCounts = new Map();
    clickEvents.forEach((event) => {
      const position = Number(event.new_values?.position);
      if (position > 0) positionCounts.set(position, (positionCounts.get(position) || 0) + 1);
    });
    const positionDistribution = [1, 2, 3, 4, 5].map((position) => ({
      position: `#${position}`,
      clickuri: positionCounts.get(position) || 0,
    }));

    const coverageStudents = debouncedStudentId === 'all'
      ? students
      : students.filter((student) => student.id === debouncedStudentId);
    const studentsWithSession = new Set(sessions.map((session) => session.studentId));
    const coverage = coverageStudents.length > 0
      ? (coverageStudents.filter((student) => studentsWithSession.has(student.id)).length / coverageStudents.length) * 100
      : null;

    const daily = new Map();
    sessions.forEach((session) => {
      const day = session.shownAt.toISOString().slice(0, 10);
      const entry = daily.get(day) || { day, shown: 0, clicked: 0 };
      entry.shown += session.suggestions.length;
      daily.set(day, entry);
    });
    clickEvents.forEach((event) => {
      const day = new Date(event.new_values?.clicked_at || event.created_at).toISOString().slice(0, 10);
      const entry = daily.get(day) || { day, shown: 0, clicked: 0 };
      entry.clicked += 1;
      daily.set(day, entry);
    });
    const evolution = [...daily.values()]
      .sort((a, b) => a.day.localeCompare(b.day))
      .map((entry) => ({ ...entry, precision: entry.shown > 0 ? (entry.clicked / entry.shown) * 100 : 0 }));

    return {
      sessionCount: sessions.length,
      shownCount: totalShown,
      clickedCount: totalClicks,
      convertedCount: converted,
      precision,
      recall,
      f1,
      ndcg,
      diversity,
      moduleDistribution,
      positionDistribution,
      coverage,
      evolution,
    };
  }, [auditEvents, dataset, students, debouncedStudentId, yearRange, tab]);

  const batchForClass = (clasa) => {
    const classStudents = students.filter((student) => student.clasa === clasa);
    const activityScores = new Map();
    classStudents.forEach((student) => {
      (recommendationsByStudent.get(student.id) || []).forEach((item) => {
        const entry = activityScores.get(item.activityName) || { name: item.activityName, module: item.module, score: 0, students: 0 };
        entry.score += item.finalScore;
        entry.students += 1;
        activityScores.set(item.activityName, entry);
      });
    });
    return [...activityScores.values()].sort((a, b) => b.score - a.score).slice(0, 5);
  };

  const handleNotifyClass = async () => {
    const { clasa, items } = batchDialog;
    const classStudents = students.filter((student) => student.clasa === clasa);
    const message = `Recomandari pentru ${clasa}: ${items.map((item) => `${item.name} (${item.module})`).join('; ')}`;
    try {
      const { error } = await supabase.from(TABLES.notifications).insert(
        classStudents.map((student) => ({
          id_user: student.id,
          titlu: 'Recomandari pentru clasa',
          mesaj: message,
          tip: 'info',
          citita: false,
        })),
      );
      if (error) throw error;
      setSnackbar({ open: true, message: `Notificare trimisa pentru ${classStudents.length} elevi.` });
    } catch (error) {
      setSnackbar({ open: true, message: error.message });
    } finally {
      setBatchDialog({ open: false, clasa: null, items: [] });
    }
  };

  const handleExportStudents = () => {
    downloadExcel(
      `statistici-elevi-${new Date().toISOString().slice(0, 10)}.xls`,
      ['Elev', 'Clasa', 'Total', ...dataset.moduleCodes, 'Diversitate', 'Percentila', 'Predictie', 'Probabilitate'],
      filteredStudents.map((student) => {
        const profile = profiles.get(student.id);
        const forecast = profile
          ? forecastEndOfYear(
              profile.monthlyCumulative,
              dataset.targetSum,
              Math.min(9, Math.max(0, schoolMonthIndex(new Date().toISOString()))),
            )
          : null;
        return [
          `${student.nume} ${student.prenume}`,
          student.clasa,
          student.total,
          ...dataset.moduleCodes.map((code) => student.modules[code] || 0),
          student.diversityScore.toFixed(3),
          student.percentile.toFixed(0),
          forecast ? Math.round(forecast.estimated) : '',
          forecast ? `${(forecast.probability * 100).toFixed(0)}%` : '',
        ];
      }),
    );
  };

  const handleResetMetrics = async () => {
    try {
      const { error } = await supabase
        .from(TABLES.audit_log)
        .delete()
        .in('entity_type', ['suggestion_shown', 'suggestion_clicked']);
      if (error) throw error;
      setAuditEvents([]);
      setSnackbar({ open: true, message: 'Metricile au fost resetate.' });
    } catch (error) {
      setSnackbar({ open: true, message: `${error.message} - ruleaza scriptul SQL pentru politica de stergere.` });
    } finally {
      setResetDialog(false);
    }
  };

  const handleExportMetrics = () => {
    downloadExcel(
      `statistici-metrici-${new Date().toISOString().slice(0, 10)}.xls`,
      ['Indicator', 'Valoare'],
      [
        ['Precizie', metrics.precision === null ? 'date insuficiente' : `${(metrics.precision * 100).toFixed(1)}%`],
        ['Recall', metrics.recall === null ? 'date insuficiente' : `${(metrics.recall * 100).toFixed(1)}%`],
        ['F1', metrics.f1 === null ? 'date insuficiente' : metrics.f1.toFixed(3)],
        ['NDCG@5', metrics.ndcg === null ? 'date insuficiente' : metrics.ndcg.toFixed(3)],
        ['Diversitate (entropie/sesiune)', metrics.diversity === null ? 'date insuficiente' : metrics.diversity.toFixed(3)],
        ['Acoperire', metrics.coverage === null ? 'date insuficiente' : `${metrics.coverage.toFixed(0)}%`],
        ['Sesiuni', metrics.sessionCount],
        ['Sugestii afisate', metrics.shownCount],
        ['Sugestii accesate', metrics.clickedCount],
        ['Sugestii convertite (30 zile)', metrics.convertedCount],
        [],
        ['Distributie module', ''],
        ...metrics.moduleDistribution.map((entry) => [entry.cod, entry.count]),
        [],
        ['Click-uri pe pozitie', ''],
        ...metrics.positionDistribution.map((entry) => [entry.position, entry.clickuri]),
      ],
    );
  };

  const drawerProfile = drawerStudent ? profiles.get(drawerStudent.id) : null;
  const drawerCluster = drawerStudent ? cluster.clusters.get(drawerStudent.id) : null;
  const drawerClassPeers = useMemo(
    () => (drawerProfile ? [...profiles.values()].filter((profile) => profile.clasa === drawerProfile.clasa) : []),
    [drawerProfile, profiles],
  );
  const drawerForecast = drawerProfile
    ? forecastEndOfYear(drawerProfile.monthlyCumulative, dataset.targetSum, Math.min(9, Math.max(0, schoolMonthIndex(new Date().toISOString()))))
    : null;

  const drawerRadarData = useMemo(
    () =>
      drawerProfile && dataset
        ? dataset.modules.map((module) => ({
            modul: module.cod_module,
            credite: drawerProfile.creditsByModule[module.cod_module] || 0,
            media: drawerClassPeers.length
              ? mean(drawerClassPeers.map((profile) => profile.creditsByModule[module.cod_module] || 0))
              : 0,
            tinta: module.credite_tinta || 0,
          }))
        : [],
    [drawerProfile, drawerClassPeers, dataset],
  );
  const drawerRadarMax = drawerRadarData.reduce(
    (maxValue, entry) => Math.max(maxValue, entry.credite, entry.media, entry.tinta),
    0,
  ) || 1;

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reincarca
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (loading || !dataset || !selectedYear) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          justifyContent="center"
          alignItems="center"
          minHeight="40vh"
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">Se calculează statisticile...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Sistem Inteligent</Typography>
          <Typography variant="body2" color="text.secondary">
            Analiza creditelor, recomandari si evaluarea algoritmilor
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 300 }}
            options={studentOptions}
            value={selectedStudentOption}
            onChange={(event, option) => setSelectedStudentId(option ? option.id : 'all')}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Caută elev după nume..." />}
          />
          {selectedStudent && (
            <Chip
              label={`${selectedStudent.nume} ${selectedStudent.prenume} (${selectedStudent.clasa || '-'})`}
              onDelete={() => setSelectedStudentId('all')}
            />
          )}
        </Box>
      </Box>

      <Tabs value={tab} onChange={(event, value) => setTab(value)} variant="scrollable" sx={{ mb: 3 }}>
        <Tab label="Dashboard" />
        <Tab label="Analiza elevilor" />
        <Tab label="Analiza claselor" />
        <Tab label="Recomandari" />
        <Tab label="Retrospectiv" />
        <Tab label="Metrici" />
      </Tabs>

      {computing && <LinearProgress sx={{ mb: 2 }} />}

      {computing ? <TabSkeleton /> : (
      <>

      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Clasa</InputLabel>
              <Select
                label="Clasa"
                value={dashFilters.clasa}
                onChange={(event) => setDashFilters({ ...dashFilters, clasa: event.target.value })}
              >
                <MenuItem value="all">Toate clasele</MenuItem>
                {classOptions.map((clasa) => <MenuItem key={clasa} value={clasa}>{clasa}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Specializare</InputLabel>
              <Select
                label="Specializare"
                value={dashFilters.spec}
                onChange={(event) => setDashFilters({ ...dashFilters, spec: event.target.value })}
              >
                <MenuItem value="all">Toate</MenuItem>
                {specOptions.map((spec) => <MenuItem key={spec} value={spec}>{spec}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
            Filtrele se aplică doar acestui tab.
          </Typography>

          {dashStudents.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Nu exista date pentru filtrele selectate.
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <SummaryCard label="Elevi" value={dashStudents.length} />
            <SummaryCard label="Credite totale" value={Math.round(dashStudents.reduce((sum, student) => sum + student.total, 0))} />
            <SummaryCard label="Medie credite/elev" value={mean(dashStudents.map((student) => student.total)).toFixed(1)} />
            <SummaryCard label="Alerte active" value={alerts.total} color="error.main" />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 3 }}>
            <ChartCard title="Trend global credite cumulate">
              <LineChart data={globalTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="luna" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="cumulat" stroke="#4F46E5" name="Cumulat" />
              </LineChart>
            </ChartCard>
            <ChartCard title="Surse activitate" height={260}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Manual', value: sourceCounts.manual },
                    { name: 'SRBHub', value: sourceCounts.srbhub },
                    { name: 'TEST', value: sourceCounts.import },
                  ]}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  label
                >
                  <Cell fill="#4F46E5" />
                  <Cell fill="#000000" />
                  <Cell fill="#10B981" />
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ChartCard>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <Card
              sx={{ borderRadius: 3, cursor: 'pointer' }}
              onClick={() =>
                setAlertsDialog({
                  open: true,
                  title: 'Elevi la risc',
                  items: alerts.atRisk.map((student) => ({
                    primary: `${student.nume} ${student.prenume} (${student.clasa || '-'})`,
                    secondary: `${student.total} credite, fara activitate in anul selectat`,
                  })),
                })
              }
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Elevi la risc ({alerts.atRisk.length})
                </Typography>
                {alerts.atRisk.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nicio alertă activă.
                  </Typography>
                )}
                {alerts.atRisk.slice(0, 3).map((student) => (
                  <Typography key={student.id} variant="body2">
                    {student.nume} {student.prenume} ({student.clasa}) - {student.total} credite
                  </Typography>
                ))}
                {alerts.atRisk.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    Vezi toți {alerts.atRisk.length} elevi
                  </Typography>
                )}
              </CardContent>
            </Card>
            <Card
              sx={{ borderRadius: 3, cursor: 'pointer' }}
              onClick={() =>
                setAlertsDialog({
                  open: true,
                  title: 'Module deficitare',
                  items: alerts.moduleGaps.map((entry) => ({
                    primary: entry.cod,
                    secondary: `deficit colectiv ${entry.deficit} credite`,
                  })),
                })
              }
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Module deficitare</Typography>
                {alerts.moduleGaps.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Nicio alertă activă.</Typography>
                )}
                {alerts.moduleGaps.map((entry) => (
                  <Typography key={entry.cod} variant="body2">
                    {entry.cod}: deficit colectiv {entry.deficit} credite
                  </Typography>
                ))}
              </CardContent>
            </Card>
            <Card
              sx={{ borderRadius: 3, cursor: 'pointer' }}
              onClick={() =>
                setAlertsDialog({
                  open: true,
                  title: 'Clase sub medie',
                  items: alerts.weakClasses.map((entry) => ({
                    primary: `Clasa ${entry.clasa}`,
                    secondary: `medie ${entry.medie.toFixed(1)} credite`,
                  })),
                })
              }
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Clase sub medie</Typography>
                {alerts.weakClasses.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Nicio alertă activă.</Typography>
                )}
                {alerts.weakClasses.map((entry) => (
                  <Typography key={entry.clasa} variant="body2">
                    Clasa {entry.clasa}: medie {entry.medie.toFixed(1)}
                  </Typography>
                ))}
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Clasa</InputLabel>
              <Select label="Clasa" value={filters.clasa} onChange={(event) => setFilters({ ...filters, clasa: event.target.value })}>
                <MenuItem value="all">Toate clasele</MenuItem>
                {classOptions.map((clasa) => <MenuItem key={clasa} value={clasa}>{clasa}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Specializare</InputLabel>
              <Select label="Specializare" value={filters.spec} onChange={(event) => setFilters({ ...filters, spec: event.target.value })}>
                <MenuItem value="all">Toate</MenuItem>
                {specOptions.map((spec) => <MenuItem key={spec} value={spec}>{spec}</MenuItem>)}
              </Select>
            </FormControl>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportStudents}>Export Excel</Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    { id: 'nume', label: 'Elev' },
                    { id: 'clasa', label: 'Clasa' },
                    { id: 'specializare', label: 'Specializare' },
                    { id: 'total', label: 'Total' },
                    { id: 'modules', label: 'Module (1-5)' },
                    { id: 'diversityScore', label: 'Diversitate' },
                    { id: 'consistency', label: 'Constanță' },
                    { id: 'percentile', label: 'Percentilă' },
                  ].map((column) => (
                    <TableCell key={column.id} sx={{ fontSize: 12, py: 0.5 }}>
                      <TableSortLabel
                        active={sort.field === column.id}
                        direction={sort.field === column.id ? sort.dir : 'asc'}
                        onClick={() => setSort((previous) => ({
                          field: column.id,
                          dir: previous.field === column.id && previous.dir === 'desc' ? 'asc' : 'desc',
                        }))}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontSize: 12, py: 0.5 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.map((student) => (
                  <TableRow key={student.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDrawerStudent(student)}>
                    <TableCell sx={{ fontSize: 12, py: 0.5 }}>{student.nume} {student.prenume}</TableCell>
                    <TableCell sx={{ fontSize: 12, py: 0.5 }}>{student.clasa || '-'}</TableCell>
                    <TableCell sx={{ fontSize: 12, py: 0.5 }}>{student.specializare || '-'}</TableCell>
                    <TableCell sx={{ fontSize: 12, py: 0.5 }}><strong>{student.total}</strong></TableCell>
                    <TableCell sx={{ fontSize: 12, py: 0.5 }}>
                      {dataset.moduleCodes
                        .map((code) => `M${code.replace('MOD', '')}: ${student.modules[code] || 0}`)
                        .join(', ')}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, py: 0.5 }}>{student.diversityScore.toFixed(2)}</TableCell>
                    <TableCell sx={{ fontSize: 12, py: 0.5 }}>{student.consistency === null ? '-' : student.consistency.toFixed(1)}</TableCell>
                    <TableCell sx={{ fontSize: 12, py: 0.5 }}>{student.percentile.toFixed(0)}</TableCell>
                    <TableCell sx={{ fontSize: 12, py: 0.5 }}>{statusChip(student.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tab === 2 && (
        <>
          <ChartCard title="Medie, mediana si max per clasa" height={280}>
            <BarChart data={classes}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="clasa" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="medie" fill="#4F46E5" name="Medie" />
              <Bar dataKey="median" fill="#10B981" name="Mediană" />
              <Bar dataKey="max" fill="#000000" name="Max" />
            </BarChart>
          </ChartCard>
          {classes.length === 0 && (
            <Alert severity="info" sx={{ my: 2 }}>Nu exista clase pentru anul selectat.</Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, my: 3 }}>
            {classes.map((entry) => (
              <Card
                key={entry.clasa}
                sx={{
                  borderRadius: 3,
                  borderColor: selectedStudent?.clasa === entry.clasa ? 'primary.main' : undefined,
                  borderWidth: selectedStudent?.clasa === entry.clasa ? 2 : undefined,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">Clasa {entry.clasa}</Typography>
                    <Chip size="small" label={`${entry.elevi} elevi`} />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="body2">Medie: <strong>{entry.medie.toFixed(1)}</strong></Typography>
                    <Typography variant="body2">Mediană: <strong>{entry.median.toFixed(1)}</strong></Typography>
                    <Typography variant="body2">Std: <strong>{entry.std.toFixed(1)}</strong></Typography>
                    <Typography variant="body2">Min: <strong>{entry.min}</strong></Typography>
                    <Typography variant="body2">Max: <strong>{entry.max}</strong></Typography>
                    <Typography variant="body2">
                      Entropie: <strong>{entry.entropie === null ? 'date insuficiente' : entry.entropie.toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body2">Gini: <strong>{entry.gini.toFixed(2)}</strong></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {entry.zero > 0 && <Chip size="small" color="error" label={`${entry.zero} elevi cu 0 credite`} />}
                    {entry.atRisk > 0 && <Chip size="small" color="error" variant="outlined" label={`${entry.atRisk} la risc`} />}
                    {entry.excellence > 0 && <Chip size="small" color="success" label={`${entry.excellence} excelenta`} />}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        !entry.hasPreviousMonth
                          ? 'Trend: prima luna'
                          : entry.trend === 0
                            ? 'Trend: constant'
                            : `Trend lunar: ${entry.trend >= 0 ? '+' : ''}${entry.trend.toFixed(0)}%`
                      }
                    />
                  </Box>
                  <Box sx={{ width: '100%', height: 80, mt: 1, minWidth: 0 }}>
                    {(entry.moduleTotals || []).reduce((sum, value) => sum + value, 0) > 0 ? (
                      <ResponsiveContainer initialDimension={{ width: 520, height: 80 }}>
                        <BarChart data={(entry.moduleTotals || []).map((value, index) => ({ cod: dataset.moduleCodes[index], credite: value || 0 }))}>
                          <XAxis dataKey="cod" hide />
                          <YAxis hide />
                          <Tooltip />
                          <Bar dataKey="credite" fill="#4F46E5" name="Credite" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (entry.categoryTotals || []).reduce((sum, value) => sum + value, 0) > 0 ? (
                      <ResponsiveContainer initialDimension={{ width: 520, height: 80 }}>
                        <BarChart data={CATEGORY_KEYS.map((key, index) => ({ categorie: CATEGORY_LABEL_MAP[key], credite: entry.categoryTotals[index] || 0 }))}>
                          <XAxis dataKey="categorie" tick={{ fontSize: 11 }} />
                          <YAxis hide />
                          <Tooltip />
                          <Bar dataKey="credite" fill="#10B981" name="Credite" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Credite reportate din anul anterior. Fără activitate nouă.
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                    <Typography variant="caption">
                      Activi: <strong>{entry.activeCount}</strong>
                    </Typography>
                    <Typography variant="caption">
                      Fără activitate: <strong>{entry.inactiveCount}</strong>
                    </Typography>
                    {entry.trend30 > 0 && (
                      <Typography variant="caption">
                        Trend 30 zile: <strong>+{entry.trend30}</strong> credite
                      </Typography>
                    )}
                    {entry.schoolDelta !== null && (
                      <Typography variant="caption">
                        vs media scolii: <strong>{entry.schoolDelta >= 0 ? '+' : ''}{entry.schoolDelta.toFixed(0)}%</strong>
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setFilters({ ...filters, clasa: entry.clasa });
                        setTab(1);
                      }}
                    >
                      Vezi elevi
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {classes.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>Date insuficiente pentru heatmap.</Alert>
          )}
          {classes
            .filter((entry) => (entry.students || []).length > 0 && Array.isArray(entry.moduleTotals))
            .slice(0, 2)
            .map((entry) => {
            const rows = entry.students || [];
            const codes = dataset.moduleCodes || [];
            const totalModuleCredits = (entry.moduleTotals || []).reduce((sum, value) => sum + value, 0);
            if (totalModuleCredits === 0) {
              return (
                <Card key={`heat-${entry.clasa}`} sx={{ borderRadius: 3, mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Heatmap elevi x module - Clasa {entry.clasa}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fără credite pe module în anul selectat.
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            const maxValue = Math.max(
              ...rows.flatMap((student) => codes.map((code) => student.modules?.[code] || 0)),
              1,
            );
            return (
              <Card key={`heat-${entry.clasa}`} sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Heatmap elevi x module - Clasa {entry.clasa}
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: `180px repeat(${dataset.moduleCodes.length}, 64px)`, gap: 0.5 }}>
                      <Typography variant="caption" fontWeight="bold">Elev</Typography>
                      {dataset.moduleCodes.map((code) => (
                        <Typography key={code} variant="caption" fontWeight="bold" align="center">{code}</Typography>
                      ))}
                      {entry.students.map((student) => (
                        <Box key={student.id} sx={{ display: 'contents' }}>
                          <Typography variant="caption" noWrap>{student.nume} {student.prenume}</Typography>
                          {dataset.moduleCodes.map((code) => (
                            <Box
                              key={`${student.id}-${code}`}
                              sx={{
                                bgcolor: heatColor(student.modules[code] || 0, maxValue),
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                textAlign: 'center',
                                fontSize: 11,
                                py: 0.5,
                              }}
                            >
                              {student.modules[code] || 0}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </>
      )}

      {tab === 3 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Recomandari agregate la nivel de clasa, pe baza scorurilor individuale.
          </Alert>
          {selectedStudent && (
            <Card sx={{ borderRadius: 3, mb: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Recomandari pentru {selectedStudent.nume} {selectedStudent.prenume}
                </Typography>
                {(recommendationsByStudent.get(selectedStudent.id) || []).map((item, index) => (
                  <Box key={`${item.activityId}-${item.module}`} sx={{ py: 0.5 }}>
                    <Typography variant="body2">
                      {index + 1}. {item.activityName} ({item.module}) - {item.estimatedCredits} credite
                    </Typography>
                    {item.explanation.map((line) => (
                      <Typography key={line} variant="caption" color="text.secondary" display="block">
                        {line}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
          {!selectedStudent && classes.map((entry) => {
            const items = batchForClass(entry.clasa);
            return (
            <Card key={entry.clasa} sx={{ borderRadius: 3, mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      Ce ar trebui sa faca clasa {entry.clasa} in continuare
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<NotifyIcon />}
                      disabled={items.length === 0}
                      onClick={() => setBatchDialog({ open: true, clasa: entry.clasa, items })}
                    >
                      Trimite notificare clasei
                    </Button>
                  </Box>
                  {items.map((item, index) => (
                    <Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2">{index + 1}. {item.name} ({item.module})</Typography>
                      <Typography variant="body2" fontWeight="bold">{item.students} elevi</Typography>
                    </Box>
                  ))}
                  {items.length === 0 && <Typography variant="body2" color="text.secondary">Fara recomandari.</Typography>}
                </CardContent>
              </Card>
            );
          })}
        </>
      )}

      {tab === 4 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <SchoolYearSelect value={retroYear} options={yearOptions} onChange={setRetroYear} />
            <SchoolYearBadge year={retroYear} />
            {retroYear === getCurrentSchoolYear() && (
              <Chip size="small" color="warning" label="an în curs — date parțiale" />
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={!yearOptions.includes(getPreviousSchoolYear(retroYear))}
              onClick={() => setRetroYear(getPreviousSchoolYear(retroYear))}
            >
              Compară cu anul anterior
            </Button>
          </Box>
          {retrospective.length === 0 && <Alert severity="info">Nu exista date pentru anul selectat.</Alert>}
          {retrospective.map(({ report, delta, currentTotal }) => (
            <Card key={report.studentId} sx={{ borderRadius: 3, mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {report.studentName} - {report.totalCredits} credite in {report.schoolYear}
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  {report.lines.map((line) => (
                    <Typography component="li" key={line} variant="body2" sx={{ mb: 1 }}>{line}</Typography>
                  ))}
                </Box>
                {delta !== null && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Fata de anul curent: {currentTotal} credite ({delta >= 0 ? '+' : ''}{delta.toFixed(0)}%).
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </>
      )}

      {tab === 5 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {profile?.rol === 'coordonator' && (
                <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setResetDialog(true)}>
                  Resetează metrici
                </Button>
              )}
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportMetrics}>Export Excel</Button>
            </Box>
          </Box>
          {metrics.sessionCount === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Nu există evenimente înregistrate pentru acest an. Sistemul loghează automat sugestiile afișate și accesate.
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <SummaryCard label="Precizie (click/afisare)" value={formatPercent(metrics.precision)} />
            <SummaryCard label="Recall (conversie 30 zile)" value={formatPercent(metrics.recall)} />
            <SummaryCard label="F1" value={formatNumber(metrics.f1)} />
            <SummaryCard label="NDCG@5" value={formatNumber(metrics.ndcg)} />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <SummaryCard label="Diversitate (entropie/sesiune)" value={formatNumber(metrics.diversity)} />
            <SummaryCard
              label="Acoperire (elevi cu sesiune)"
              value={metrics.coverage === null ? 'date insuficiente' : metrics.coverage.toFixed(0)}
              suffix={metrics.coverage === null ? '' : '%'}
            />
            <SummaryCard label="Sesiuni" value={metrics.sessionCount} />
            <SummaryCard label="Sugestii afisate" value={metrics.shownCount} />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <SummaryCard label="Sugestii accesate" value={metrics.clickedCount} />
            <SummaryCard label="Sugestii convertite (30 zile)" value={metrics.convertedCount} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <ChartCard title="Click-uri pe pozitie in top 5" height={220}>
              <BarChart data={metrics.positionDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="position" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="clickuri" fill="#4F46E5" name="Click-uri" />
              </BarChart>
            </ChartCard>
            <ChartCard title="Distributia modulelor sugerate" height={220}>
              <BarChart data={metrics.moduleDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="cod" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#10B981" name="Sugestii" />
              </BarChart>
            </ChartCard>
          </Box>
          <ChartCard title="Evolutie pe zile: sugestii afisate, accesate, precizie" height={280}>
            <LineChart data={metrics.evolution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="shown" stroke="#4F46E5" name="Sugestii afisate" />
              <Line type="monotone" dataKey="clicked" stroke="#000000" name="Sugestii accesate" />
              <Line type="monotone" dataKey="precision" stroke="#10B981" name="Precizie %" />
            </LineChart>
          </ChartCard>
        </>
      )}
      </>
      )}

      <Drawer anchor="right" open={Boolean(drawerStudent)} onClose={() => setDrawerStudent(null)}>
        <Box sx={{ width: { xs: 320, sm: 440 }, p: 3 }}>
          {drawerProfile && (
            <>
              <Typography variant="h6" fontWeight="bold">
                {drawerProfile.nume} {drawerProfile.prenume}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Clasa {drawerProfile.clasa || '-'} • {drawerProfile.specializare || '-'} • {drawerProfile.totalCredits} credite
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ width: '100%', height: 260, minWidth: 0 }}>
                {drawerRadarData.length > 0 && (
                <ResponsiveContainer initialDimension={{ width: 440, height: 260 }}>
                  <RadarChart data={drawerRadarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="modul" />
                    <PolarRadiusAxis domain={[0, Math.ceil(drawerRadarMax * 1.2)]} />
                    <Radar dataKey="credite" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.5} name="Credite" />
                    <Radar dataKey="media" stroke="#10B981" fill="none" strokeDasharray="5 5" name="Media clasei" />
                    <Radar dataKey="tinta" stroke="#000000" fill="#000000" fillOpacity={0.1} name="Tinta" />
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
                )}
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Cluster peer group: <strong>{drawerCluster !== undefined ? drawerCluster + 1 : '-'}</strong> cu{' '}
                <strong>{drawerCluster !== undefined ? cluster.members[drawerCluster]?.length || 0 : 0}</strong> elevi similari.
              </Typography>
              {drawerForecast && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Predictie iunie: <strong>{Math.round(drawerForecast.estimated)}</strong> credite,
                  probabilitate tinta <strong>{(drawerForecast.probability * 100).toFixed(0)}%</strong>.
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Top 5 recomandari live
              </Typography>
              {drawerRecommendations.map((item) => (
                <Box key={`${item.activityId}-${item.module}`} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" fontWeight="medium">{item.activityName} ({item.module})</Typography>
                  {item.explanation.map((line) => (
                    <Typography key={line} variant="caption" color="text.secondary" display="block">
                      {line}
                    </Typography>
                  ))}
                </Box>
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                sx={{ mt: 2 }}
                onClick={() => {
                  const recommendations = drawerRecommendations;
                  downloadExcel(
                    `elev-${drawerProfile.nume}-${drawerProfile.prenume}-${new Date().toISOString().slice(0, 10)}.xls`,
                    ['Indicator', 'Valoare'],
                    [
                      ['PROFIL', ''],
                      ['Elev', `${drawerProfile.nume} ${drawerProfile.prenume}`],
                      ['Clasa', drawerProfile.clasa || ''],
                      ['Total credite', drawerProfile.totalCredits],
                      [],
                      ['CREDITE', ''],
                      ...dataset.moduleCodes.map((code) => [`Credite ${code}`, drawerProfile.creditsByModule[code] || 0]),
                      ...Object.entries(drawerProfile.creditsByCategory).map(([key, value]) => [`Categorie ${key}`, value]),
                      [],
                      ['ACTIVITATI', ''],
                      ...drawerProfile.activityNames.map((name, index) => [`Activitate ${index + 1}`, name]),
                      [],
                      ['RECOMANDARI', ''],
                      ...recommendations.map((item, index) => [
                        `Recomandare ${index + 1}`,
                        `${item.activityName} (${item.module}) - ${item.estimatedCredits} credite, scor ${item.finalScore}; ${item.explanation.join(' | ')}`,
                      ]),
                      [],
                      ['PREDICTIE', ''],
                      ['Predictie iunie', drawerForecast ? Math.round(drawerForecast.estimated) : ''],
                      ['Probabilitate tinta', drawerForecast ? `${(drawerForecast.probability * 100).toFixed(0)}%` : ''],
                    ],
                  );
                }}
              >
                Export statistici elev (CSV)
              </Button>
            </>
          )}
        </Box>
      </Drawer>

      <Dialog open={batchDialog.open} onClose={() => setBatchDialog({ open: false, clasa: null, items: [] })} maxWidth="sm" fullWidth>
        <DialogTitle>Trimite notificare clasei {batchDialog.clasa}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>Se trimite catre toti elevii clasei:</Typography>
          {batchDialog.items.map((item) => (
            <Typography key={item.name} variant="body2">- {item.name} ({item.module})</Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialog({ open: false, clasa: null, items: [] })}>Anuleaza</Button>
          <Button variant="contained" onClick={handleNotifyClass}>Trimite</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={alertsDialog.open}
        onClose={() => setAlertsDialog({ open: false, title: '', items: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{alertsDialog.title}</DialogTitle>
        <DialogContent>
          {alertsDialog.items.length === 0 && (
            <Typography variant="body2" color="text.secondary">Nicio alertă activă.</Typography>
          )}
          {alertsDialog.items.map((item, index) => (
            <Box key={`${item.primary}-${index}`} sx={{ py: 0.5 }}>
              <Typography variant="body2" fontWeight="medium">{item.primary}</Typography>
              <Typography variant="caption" color="text.secondary">{item.secondary}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertsDialog({ open: false, title: '', items: [] })}>Închide</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetDialog} onClose={() => setResetDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Resetează metrici</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se șterg toate evenimentele suggestion_shown și suggestion_clicked. Metricile pornesc de la zero.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog(false)}>Anulează</Button>
          <Button color="error" variant="contained" onClick={handleResetMetrics}>Șterge</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
        message={snackbar.message}
      />
    </Container>
  );
}
